import { z } from "zod";

/** Shared enums — source: _truth/01-domain-erd.md §1. Stored as text + CHECK in the database. */
function en<const T extends readonly [string, ...string[]]>(values: T) {
  return { values, schema: z.enum(values) } as const;
}

export const TenantState = en([
  "pending_consent",
  "active",
  "read_only",
  "archived",
  "uninstalled",
  "consent_lost",
  "purge_scheduled",
  "purged",
]);
export const Suspension = en(["none", "suspended", "blocked"]);
export const TenantRole = en(["admin", "viewer"]);
export const PlanKey = en(["starter", "business", "scale", "enterprise"]);
export const SubscriptionStatus = en(["trial", "active", "past_due", "expired", "cancelled"]);
export const BillingInterval = en(["monthly", "annual"]);
export const TeamStatus = en(["active", "blocked_limit", "deactivated", "team_unavailable"]);
export const ChannelType = en(["standard", "private", "shared"]);
export const ScanTrigger = en(["activation", "manual", "scheduled"]);
export const ScanResult = en(["success", "partial", "failed"]);
export const Presence = en(["detected", "no_longer_detected", "removal_completed"]);
export const ReviewOutcome = en([
  "not_reviewed",
  "keep",
  "extend",
  "transfer_sponsor",
  "remove_requested",
  "exception",
]);
export const DecisionKind = en(["keep", "extend", "remove", "exception", "transfer_sponsor"]);
export const RiskType = en([
  "no_sponsor",
  "no_expiry",
  "no_reason",
  "expired",
  "expiring_soon",
  "overdue_review",
  "unknown_domain",
  "removal_requested",
  "exception",
]);
export const DomainClass = en(["trusted", "known_partner", "personal", "unknown", "watchlist"]);
export const CampaignStatus = en(["in_progress", "completed", "closed_incomplete", "cancelled"]);
export const ItemState = en(["open", "decided", "auto_closed", "cancelled", "not_reviewed"]);
export const ItemSource = en(["campaign", "ad_hoc", "exception_reopen"]);
export const RemovalStatus = en([
  "pending",
  "scheduled",
  "executing",
  "completed",
  "failed",
  "rejected",
  "cancelled",
]);
export const RemovalMode = en(["off", "team_membership", "entra_account"]);
export const ExceptionStatus = en(["pending_approval", "active", "expired", "rejected", "revoked"]);
export const ActorType = en(["user", "system"]);
export const NotificationChannel = en(["in_app", "activity_feed", "email"]);
export const NotificationStatus = en(["queued", "sent", "failed", "skipped"]);
export const ExportFormat = en(["csv", "xlsx", "pdf", "zip"]);
export const ExportScope = en(["team", "campaign", "all", "domains", "complete"]);
export const ExportStatus = en(["queued", "ready", "failed"]);
export const GrantKind = en([
  "base",
  "removal_team_membership",
  "removal_entra_account",
  "activation_install",
]);
export const GrantStatus = en(["granted", "pending", "lost"]);
export const CustomerStatus = en(["pending", "trial", "active", "past_due", "churned"]);
export const InvoiceStatus = en(["paid", "pending", "failed", "refunded"]);
export const FlagScope = en(["all", "trial_only", "beta_opt_in"]);
export const OverrideType = en(["custom_price", "teams_limit"]);
export const PurgeTrigger = en(["uninstall_1y", "long_stop", "request"]);
export const LifecycleRequestType = en(["export", "purge", "anonymise_person"]);
export const ReviewFrequency = en(["monthly", "quarterly", "semiannual", "annual"]);
export const ReviewFrequencyMode = en(["remind_admin", "auto_launch"]);
export const ScanCadence = en(["daily", "weekly"]);
export const Locale = en(["en", "fr", "de", "es"]);
export const BuiltInReasonKey = en([
  "client_engagement",
  "vendor_integration",
  "partner_collaboration",
  "advisory_contract",
  "project_delivery",
  "other",
]);
export const SuspensionReasonCategory = en([
  "payment",
  "abuse",
  "fraud",
  "legal",
  "customer_request",
  "other",
]);

export type TenantState = z.infer<typeof TenantState.schema>;
export type Suspension = z.infer<typeof Suspension.schema>;
export type TenantRole = z.infer<typeof TenantRole.schema>;
export type PlanKey = z.infer<typeof PlanKey.schema>;
export type SubscriptionStatus = z.infer<typeof SubscriptionStatus.schema>;
export type BillingInterval = z.infer<typeof BillingInterval.schema>;
export type TeamStatus = z.infer<typeof TeamStatus.schema>;
export type ChannelType = z.infer<typeof ChannelType.schema>;
export type ScanTrigger = z.infer<typeof ScanTrigger.schema>;
export type ScanResult = z.infer<typeof ScanResult.schema>;
export type Presence = z.infer<typeof Presence.schema>;
export type ReviewOutcome = z.infer<typeof ReviewOutcome.schema>;
export type DecisionKind = z.infer<typeof DecisionKind.schema>;
export type RiskType = z.infer<typeof RiskType.schema>;
export type DomainClass = z.infer<typeof DomainClass.schema>;
export type CampaignStatus = z.infer<typeof CampaignStatus.schema>;
export type ItemState = z.infer<typeof ItemState.schema>;
export type ItemSource = z.infer<typeof ItemSource.schema>;
export type RemovalStatus = z.infer<typeof RemovalStatus.schema>;
export type RemovalMode = z.infer<typeof RemovalMode.schema>;
export type ExceptionStatus = z.infer<typeof ExceptionStatus.schema>;
export type ActorType = z.infer<typeof ActorType.schema>;
export type NotificationChannel = z.infer<typeof NotificationChannel.schema>;
export type NotificationStatus = z.infer<typeof NotificationStatus.schema>;
export type ExportFormat = z.infer<typeof ExportFormat.schema>;
export type ExportScope = z.infer<typeof ExportScope.schema>;
export type ExportStatus = z.infer<typeof ExportStatus.schema>;
export type GrantKind = z.infer<typeof GrantKind.schema>;
export type GrantStatus = z.infer<typeof GrantStatus.schema>;
export type CustomerStatus = z.infer<typeof CustomerStatus.schema>;
export type InvoiceStatus = z.infer<typeof InvoiceStatus.schema>;
export type FlagScope = z.infer<typeof FlagScope.schema>;
export type OverrideType = z.infer<typeof OverrideType.schema>;
export type PurgeTrigger = z.infer<typeof PurgeTrigger.schema>;
export type LifecycleRequestType = z.infer<typeof LifecycleRequestType.schema>;
export type ReviewFrequency = z.infer<typeof ReviewFrequency.schema>;
export type ReviewFrequencyMode = z.infer<typeof ReviewFrequencyMode.schema>;
export type ScanCadence = z.infer<typeof ScanCadence.schema>;
export type Locale = z.infer<typeof Locale.schema>;
export type BuiltInReasonKey = z.infer<typeof BuiltInReasonKey.schema>;
export type SuspensionReasonCategory = z.infer<typeof SuspensionReasonCategory.schema>;

/** SQL CHECK fragment for an enum column, e.g. checkSql("state", TenantState). */
export function checkSql(column: string, e: { readonly values: readonly string[] }): string {
  return `${column} IN (${e.values.map((v) => `'${v}'`).join(", ")})`;
}
